<template>
  <div class="menu-view">
    <AppHeader>
      <template #bottom>
        <div class="categories-nav" ref="navContainer" v-if="!menuStore.isLoading">
          <button 
            v-for="cat in menuStore.getAllCategories" 
            :key="cat.id"
            class="cat-pill"
            :class="{ active: activeCategory === cat.id }"
            @click="scrollToCategory(cat.id)"
            :ref="el => categoryRefs[cat.id] = el"
          >
            {{ getCategoryName(cat) }}
          </button>
        </div>
      </template>
    </AppHeader>

    <!-- Loading State -->
    <div v-if="menuStore.isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ $t('loadingMenu') }}</p>
    </div>

    <!-- Menu Content -->
    <div v-else class="products-container">
      <div 
        v-for="cat in menuStore.getAllCategories" 
        :key="cat.id"
        :id="`category-${cat.id}`"
        class="category-section"
        :ref="el => sectionRefs[cat.id] = el"
      >
        <h2 class="category-title">{{ getCategoryName(cat) }}</h2>
        <transition-group name="staggered-fade" tag="div" class="products-grid">
          <div 
            v-for="(item, index) in menuStore.getItemsByCategory(cat.id)" 
            :key="item.id"
            class="product-card"
            :style="{ '--index': index }"
            @click="openProduct(item, cat.id)"
          >
            <div class="product-image">
              <img :src="item.image" :alt="item.name" loading="lazy" />
              <div class="add-btn-overlay">
                <span>+</span>
              </div>
            </div>
            <div class="product-info">
              <h3>{{ item.name }}</h3>
              <p class="description">{{ truncate(item.description, 50) }}</p>
              <p class="price">{{ formatPrice(item.price) }}</p>
            </div>
          </div>
        </transition-group>
      </div>
    </div>

    <ProductCustomizerModal
      v-if="selectedProduct"
      :item="selectedProduct"
      :is-open="!!selectedProduct"
      @close="selectedProduct = null"
      @add-to-cart="handleAddToCart"
    />

    <AgeVerificationModal
      :is-open="showAgeVerification"
      @confirm="handleAgeConfirm"
      @reject="handleAgeReject"
      @close="showAgeVerification = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMenuStore } from '@/stores/menu'
import { useTableStore } from '@/stores/table'
import { useSessionStore } from '@/stores/session'
import { formatPrice } from '@/utils/formatters'
import AppHeader from '@/components/AppHeader.vue'
import ProductCustomizerModal from '@/components/ProductCustomizerModal.vue'
import AgeVerificationModal from '@/components/AgeVerificationModal.vue'

const route = useRoute()
const menuStore = useMenuStore()
const tableStore = useTableStore()
const sessionStore = useSessionStore()
const { t, te } = useI18n()

const activeCategory = ref(null)
const selectedProduct = ref(null)
const selectedProductCategoryId = ref(null)
const showAgeVerification = ref(false)
const pendingCartAction = ref(null)
const navContainer = ref(null)
const categoryRefs = ref({})
const sectionRefs = ref({})
let observer = null

/**
 * Get category display name
 * For static menu: use i18n translation
 * For API menu: use name directly from API
 */
const getCategoryName = (cat) => {
  // If menu is from API, use the name directly
  if (menuStore.isFromApi) {
    return cat.name
  }
  // For static menu, try i18n translation, fallback to name
  return te(cat.id) ? t(cat.id) : cat.name
}

const openProduct = (item, categoryId) => {
  selectedProduct.value = item
  selectedProductCategoryId.value = categoryId
}

const handleAddToCart = async ({ product, customizations, finalPrice }) => {
  const categoryId = selectedProductCategoryId.value
  
  // Check if this is an alcohol category and user hasn't verified age yet
  if (menuStore.isAlcoholCategory(categoryId) && !menuStore.ageVerified) {
    // Store the pending action and show age verification modal
    pendingCartAction.value = { product, customizations, finalPrice }
    selectedProduct.value = null // Close the product modal
    showAgeVerification.value = true
    return
  }
  
  // Proceed with adding to cart
  await addItemToCart({ product, customizations, finalPrice })
}

const addItemToCart = async ({ product, customizations, finalPrice }) => {
  try {
    await tableStore.addItemToCart(product, customizations, finalPrice)
    selectedProduct.value = null
  } catch (error) {
    console.error('Failed to add to cart:', error)
    alert('Failed to add item. Please try again.')
  }
}

const handleAgeConfirm = async () => {
  showAgeVerification.value = false
  menuStore.setAgeVerified(true)
  
  // Execute the pending cart action
  if (pendingCartAction.value) {
    await addItemToCart(pendingCartAction.value)
    pendingCartAction.value = null
  }
}

const handleAgeReject = () => {
  showAgeVerification.value = false
  pendingCartAction.value = null
}

const truncate = (text, length) => {
  if (text.length <= length) return text
  return text.substring(0, length) + '...'
}

const scrollToCategory = (catId) => {
  const element = document.getElementById(`category-${catId}`)
  if (element) {
    // Offset for sticky header + nav
    const headerOffset = 150
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
    
    // Manually set active category to avoid observer lag
    activeCategory.value = catId
    scrollNavToActive(catId)
  }
}

const scrollNavToActive = (catId) => {
  const navEl = navContainer.value
  const activeEl = categoryRefs.value[catId]
  
  if (navEl && activeEl) {
    const navRect = navEl.getBoundingClientRect()
    const activeRect = activeEl.getBoundingClientRect()
    
    // Calculate scroll position to center the active element
    const scrollLeft = activeEl.offsetLeft - (navRect.width / 2) + (activeRect.width / 2)
    
    navEl.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    })
  }
}

const setupIntersectionObserver = () => {
  const options = {
    root: null,
    rootMargin: '-150px 0px -70% 0px', // Adjusted for fixed header
    threshold: 0
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Extract category ID from element ID (category-xyz -> xyz)
        const catId = entry.target.id.replace('category-', '')
        activeCategory.value = catId
        scrollNavToActive(catId)
      }
    })
  }, options)

  // Observe all category sections
  Object.values(sectionRefs.value).forEach((el) => {
    if (el) observer.observe(el)
  })
}

// Bind table store when view mounts
onMounted(async () => {
  // Get restaurant slug from route param (primary) or session (fallback)
  const restaurantSlug = route.params.restaurantSlug || sessionStore.restaurantSlug
  
  if (sessionStore.sessionId) {
    tableStore.bindSession(sessionStore.sessionId)
  }
  
  // Load menu from API if we have a restaurant context
  if (restaurantSlug) {
    await menuStore.loadMenu(restaurantSlug)
  }
  
  // Set initial active category after menu is loaded
  if (menuStore.getAllCategories.length > 0) {
    activeCategory.value = menuStore.getAllCategories[0]?.id
  }
  
  nextTick(() => {
    setupIntersectionObserver()
  })
})

onUnmounted(() => {
  tableStore.unbindTable()
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.menu-view {
  padding-bottom: 100px;
  padding-top: 140px; /* Space for fixed header + nav */
  min-height: 100vh;
  background-color: var(--bg-color);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.categories-nav {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding: 0 1rem 1rem 1rem; /* Add bottom padding */
  gap: 0.75rem;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  background: white; /* Ensure background matches header */
  width: 100%;
}

.categories-nav::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.cat-pill {
  white-space: nowrap;
  padding: 10px 20px;
  border-radius: 25px;
  border: 1px solid var(--border-color);
  background: white;
  color: var(--text-light);
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0; /* Prevent shrinking */
}

.cat-pill.active {
  background: var(--secondary-color);
  color: white;
  border-color: var(--secondary-color);
  transform: scale(1.05);
  box-shadow: var(--shadow-md);
}

.products-container {
  padding: 0 1rem;
}

.category-section {
  margin-bottom: 40px;
  scroll-margin-top: 150px; /* Ensure scroll lands correctly with sticky header */
}

.category-title {
  font-size: 1.5rem;
  color: var(--secondary-color);
  margin-bottom: 16px;
  padding-left: 4px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
}

.product-card {
  background: white;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  border: 1px solid transparent;
  animation: fadeInUp 0.5s ease-out backwards;
  animation-delay: calc(var(--index) * 50ms);
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: rgba(0,0,0,0.05);
}

.product-image {
  height: 140px;
  background: #f1f5f9;
  position: relative;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.add-btn-overlay {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-md);
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.product-card:hover .add-btn-overlay {
  opacity: 1;
  transform: translateY(0);
}

.add-btn-overlay span {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 1.2rem;
}

.product-info {
  padding: 12px;
}

.product-info h3 {
  font-size: 1rem;
  margin-bottom: 4px;
  color: var(--secondary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.description {
  font-size: 0.8rem;
  color: var(--text-light);
  margin-bottom: 8px;
  line-height: 1.3;
  height: 2.6em; /* 2 lines */
  overflow: hidden;
}

.price {
  font-weight: 700;
  color: var(--primary-color);
  font-size: 1.1rem;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
